/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Theme for antd
 */
import { ThemeConfig, theme as defaultTheme } from 'antd';
import { ProTokenType } from '@ant-design/pro-components';

const VAR = {
	PRIMARY_COLOR: '#2759CD',
	PRIMARY_COLOR_1: '#F0F7FF',
	PRIMARY_COLOR_2: '#D4E6FF',
	PRIMARY_COLOR_3: '#A2C2F2',
	PRIMARY_COLOR_7: '#173DA6',
	SECONDARY_COLOR: '#EC9A29',
	WHITE_COLOR: '#FFFFFF',
	TEXT_COLOR: '#232835',
	LABEL_COLOR: '#595959',
	SUCCESS_COLOR: '#52C41A',
	SUCCESS_BORDER_COLOR: '#95DE64',
	SUCCESS_BG_COLOR: '#F6FFED',
	MENU_ITEM_TEXT_COLOR: '#232835',
	ICON_COLOR: '#8C8C8C',
	ICON_HOVER_BG: '#F0F0F0',
	TABLE_BG: '#F5F5F5',
	MODAL_BG: '#FAFAFA',
	LAYOUT_BG: 'linear-gradient(250deg, #EBF3FF 31.51%, #FFF7E2 96.19%)',
	DANGER_COLOR: '#FF4D4F',
	DANGER_COLOR_HOVER: '#FF7875',
	PRO_HEADER_BG: '#020A33',
	DISABLED_COLOR: '#BFBFBF',
	DISABLED_BG: '#F0F0F0',
	SOLID_PRIMARY_HOVER: '#173da6',
	SOLID_PRIMARY_ACTIVE: '#4c7bd9',
};

//  ['#F0F7FF', '#D4E6FF', '#A2C2F2', '#759EE6', '#4C7BD9', '#2759CD', '#173DA6', '#0B2780', '#041559', '#020A33'];

const theme: ThemeConfig = {
	algorithm: defaultTheme.defaultAlgorithm,
	token: {
		fontFamily: 'Inter',
		colorPrimary: VAR.PRIMARY_COLOR,
		colorInfo: VAR.PRIMARY_COLOR,
		colorText: VAR.TEXT_COLOR,
		colorError: VAR.DANGER_COLOR,
		colorErrorHover: VAR.DANGER_COLOR_HOVER,
		colorTextDisabled: VAR.DISABLED_COLOR,
		controlItemBgHover: VAR.PRIMARY_COLOR_1,
		fontSize: 14,
		borderRadius: 6,
		colorLinkHover: VAR.SOLID_PRIMARY_HOVER,
		colorLinkActive: VAR.SOLID_PRIMARY_ACTIVE,
	},
	components: {
		Button: {
			primaryShadow: '0px 4px 6px 0px #2759CD33',
			defaultShadow: '0px 1px 6px -1px #00000005, 0px 1px 2px 0px #00000008, 0px 2px 4px 0px #00000005',
			defaultBorderColor: 'transparent',
			colorSuccess: VAR.SUCCESS_COLOR,
			colorSuccessBg: VAR.SUCCESS_BG_COLOR,
			defaultColor: VAR.ICON_COLOR,
			colorBgContainerDisabled: VAR.DISABLED_BG,
			colorPrimaryActive: VAR.SOLID_PRIMARY_ACTIVE,
			colorPrimaryHover: VAR.SOLID_PRIMARY_HOVER,
		},
		Segmented: {
			trackBg: VAR.TABLE_BG,
			itemColor: VAR.MENU_ITEM_TEXT_COLOR,
			itemSelectedBg: VAR.PRIMARY_COLOR_2,
			itemSelectedColor: VAR.PRIMARY_COLOR_7,
		},
		Tabs: {
			cardBg: VAR.WHITE_COLOR,
			borderRadiusLG: 2,
			lineWidth: 0,
			itemHoverColor: VAR.SOLID_PRIMARY_HOVER,
			itemActiveColor: VAR.SOLID_PRIMARY_ACTIVE,
		},
		Descriptions: {
			labelColor: VAR.LABEL_COLOR,
		},
		Form: {
			labelColor: VAR.LABEL_COLOR,
		},
		Tag: {
			fontSize: 12,
			fontSizeSM: 12,
		},
		Typography: {
			colorTextDescription: VAR.ICON_COLOR,
			colorTextSecondary: VAR.ICON_COLOR,
			fontWeightStrong: 500,
		},
		Result: {
			colorTextDescription: VAR.ICON_COLOR,
		},
		Splitter: {
			splitBarSize: 0,
		},
		Modal: {
			// Remove padding from modal
			paddingContentVertical: 0,
			paddingContentVerticalLG: 0,
			paddingContentVerticalSM: 0,
			paddingContentHorizontal: 0,
			paddingContentHorizontalLG: 0,
			paddingContentHorizontalSM: 0,
			padding: 0,
			paddingXXS: 0,
			paddingXS: 0,
			paddingMD: 0,
			paddingLG: 0,
			paddingSM: 0,
			paddingXL: 0,

			// Remove margin from modal, also controlling row gap in paragraphs
			marginXS: 0,
			marginSM: 0,

			titleFontSize: 20,
			contentBg: VAR.MODAL_BG,
			headerBg: VAR.WHITE_COLOR,
			footerBg: VAR.WHITE_COLOR,
		},
		Table: {
			headerBg: VAR.TABLE_BG,
			headerColor: VAR.LABEL_COLOR,
		},
		Card: {
			paddingSM: 16,
			headerPaddingSM: 16,
			headerHeightSM: 48,
			bodyPaddingSM: 16,
			headerFontSizeSM: 16,
			padding: 16,
			headerPadding: 16,
			headerHeight: 56,
			bodyPadding: 16,
			headerFontSize: 16,
		},
		Dropdown: {
			controlItemBgHover: VAR.PRIMARY_COLOR_1,
			controlItemBgActive: VAR.PRIMARY_COLOR_1,
		},
	},
	cssVar: true,
	hashed: false,
};

const proLayoutTheme: ProTokenType['layout'] = {
	bgLayout: VAR.LAYOUT_BG,
	colorPrimary: VAR.PRIMARY_COLOR,
	header: {
		heightLayoutHeader: 90,
		colorBgHeader: VAR.PRO_HEADER_BG,
	},
	sider: {
		colorMenuBackground: VAR.WHITE_COLOR,
		colorTextMenu: VAR.LABEL_COLOR,
		colorTextMenuActive: VAR.PRIMARY_COLOR,
		colorTextMenuItemHover: VAR.PRIMARY_COLOR,
		colorTextMenuSelected: VAR.PRIMARY_COLOR_7,
		colorBgMenuItemSelected: VAR.PRIMARY_COLOR_2,
		paddingBlockLayoutMenu: 10,
	},
	pageContainer: {
		paddingInlinePageContainerContent: 30,
		paddingBlockPageContainerContent: 8,
	},
};

const proLayoutHeaderTheme: ProTokenType['layout'] = {
	colorPrimary: VAR.PRIMARY_COLOR,
	header: {
		colorTextMenuSelected: VAR.SECONDARY_COLOR,
		colorTextMenuActive: VAR.WHITE_COLOR,
		colorTextMenu: VAR.PRIMARY_COLOR_3,
	},
};

const settingLayoutTheme: ProTokenType['layout'] = {
	bgLayout: VAR.LAYOUT_BG,
	colorPrimary: VAR.PRIMARY_COLOR,
	sider: {
		...proLayoutTheme.sider,
		menuHeight: 32,
	},
	pageContainer: {
		paddingInlinePageContainerContent: 30,
		paddingBlockPageContainerContent: 8,
	},
};

export { theme, proLayoutTheme, proLayoutHeaderTheme, settingLayoutTheme, VAR as VARIABLES };
